import { computed, Injectable, Optional, signal, Signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GrowthBook, type Attributes, type Plugin } from '@growthbook/growthbook';
import { autoAttributesPlugin, growthbookTrackingPlugin } from '@growthbook/growthbook/plugins';
import { environment } from '@environment/environment';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { datadogRum } from '@datadog/browser-rum';

@Injectable({
    providedIn: 'root',
})
export class GrowthBookService {
    private gb: GrowthBook | null = null;
    private initPromise: Promise<void> | null = null;
    private readonly tick = signal(0);

    constructor(private router: Router, @Optional() private angulartics2GoogleTagManager: Angulartics2GoogleTagManager) {
        const remoteEnabled = this.isGrowthBookRemoteEnabled();

        if (remoteEnabled) {
            try {
                this.gb = new GrowthBook({
                    apiHost: environment.GROWTHBOOK_API_HOST,
                    clientKey: environment.GROWTHBOOK_CLIENT_KEY,
                    decryptionKey: environment.GROWTHBOOK_DECRYPTION_KEY,
                    enableDevMode: environment.ENV !== 'production',
                    attributes: this.getAttributes(),
                    plugins: this.getPlugins(remoteEnabled),
                    onFeatureUsage: (featureKey, result) => {
                        try {
                            datadogRum.addFeatureFlagEvaluation(featureKey, result.value);
                        } catch (error) {
                            /* RUM might not be initialized */
                            if (!environment.production) {
                                console.error('RUM might not be initialized', error);
                            }
                        }
                    },
                    trackingCallback: (experiment, result) => {
                        // this.angulartics2GoogleTagManager?.eventTrack('Experiment Viewed', {
                        //     category: 'GrowthBook',
                        //     experimentId: experiment.key,
                        //     variationId: result.key,
                        // });
                        try {
                            datadogRum.addAction('growthbook_experiment_viewed', {
                                experimentId: experiment.key,
                                variationId: result.key,
                            });
                        } catch (error) {
                            /* RUM might not be initialized */
                            if (!environment.production) {
                                console.error('RUM might not be initialized', error);
                            }
                        }
                    },
                });
            } catch (error) {
                if (!environment.production) {
                    console.error('[GrowthBook] client construction failed; falling back to defaults', error);
                }
                this.gb = new GrowthBook();
            }
            this.subscribeToRouter();
        }
    }

    /**
     * Initialize GrowthBook (fetch features). Trigger at bootstrap without returning this promise.
     * When disabled, resolves immediately.
     */
    async init(timeoutMs = 2000): Promise<void> {
        if (this.initPromise !== null) {
            return this.initPromise;
        }
        if (!this.gb) {
            this.initPromise = Promise.resolve();
            return this.initPromise;
        }
        this.initPromise = this.gb
            .init({ timeout: timeoutMs, streaming: true })
            .then((res) => {
                if (res.success) {
                    this.gb?.subscribe(() => {
                        this.bump();
                    });
                    this.bump();
                }
                if (!environment.production) {
                    if (res.success) {
                        console.log('[GrowthBook] init ok, source:', res.source, 'streaming: true');
                    } else {
                        console.warn('[GrowthBook] init failed:', res.error?.message, 'source:', res.source);
                    }
                }
            })
            .catch((err) => {
                if (!environment.production) {
                    console.warn('[GrowthBook] init error:', err);
                }
            });
        return this.initPromise;
    }

    /**
     * Reactive boolean feature flag.
     */
    watchIsOn(featureKey: string): Signal<boolean> {
        return computed(() => {
            this.tick();
            return this.gb ? this.gb.isOn(featureKey) : false;
        });
    }

    /**
     * Set user attributes for targeting (call after login / profile load).
     */
    setAttributes(attributes: Record<string, string | number | boolean | null | undefined>): void {
        if (this.gb) {
            this.gb.setAttributes({
                ...this.getAttributes(),
                ...attributes,
            });
        }
        this.bump();
    }

    /**
     * Clear user-specific attributes on logout.
     */
    clearUser(): void {
        this.setAttributes({
            loggedIn: false,
            id: null,
            isAdmin: null,
        });
    }

    private bump(): void {
        this.tick.update((value) => value + 1);
    }

    private isGrowthBookRemoteEnabled(): boolean {
        return Boolean(
            environment.GROWTHBOOK_ENABLED && environment.GROWTHBOOK_API_HOST && environment.GROWTHBOOK_CLIENT_KEY && environment.GROWTHBOOK_DECRYPTION_KEY
        );
    }

    private getPlugins(remoteEnabled: boolean): Plugin[] {
        return remoteEnabled ? [autoAttributesPlugin(), growthbookTrackingPlugin()] : [autoAttributesPlugin()];
    }

    private getAttributes(): Attributes {
        return this.gb?.getAttributes() ?? { loggedIn: false };
    }

    /**
     * Update URL for Visual Editor experiments (called on route change).
     * URL Redirect experiments are not configured.
     */
    setUrl(url: string): void {
        if (!this.gb) return;
        this.gb.setURL(url);
    }

    private subscribeToRouter(): void {
        this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
            if (typeof window !== 'undefined' && window.location?.href) {
                this.setUrl(window.location.href);
            }
        });
    }
}
